import { useState, useMemo, useEffect } from "react";
import { dismissModal } from "@/utils/modalClose";
import { createPortal } from "react-dom";
import {
  IonHeader, IonToolbar, IonTitle, IonButtons, IonButton, IonIcon,
  IonSpinner, IonNote, IonItem, IonRadioGroup, IonRadio,
} from "@ionic/react";
import { closeOutline, lockClosedOutline, checkmarkCircle, cloudDownloadOutline } from "ionicons/icons";
import { userHeaders, isUserLoggedIn } from "@/utils/userAuth";
import { cardTitle, cardName, cardNote } from "./CardForm";
import {
  useStripe, useElements, CardNumberElement, CardExpiryElement, CardCvcElement,
  PaymentRequestButtonElement,
} from "@stripe/react-stripe-js";
import { nativePost } from "@/utils/nativeHttp";
import { showErrorToast } from "@/utils/toast";
import { getAttribution, trackEvent } from "@/utils/trackPageView";
import { useWalletPayment } from "./walletPayment";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || "";

// The create-payment-intent body for a checkout. Exit-intent offers and
// post-purchase upsells are server-enforced: we send the BASE price plus a
// flag (and, for offers, the user's token) and the backend re-derives the
// discount from the buyer's window, so the shown price is the charged price
// and an expired offer can't be replayed. Shared with the paywall's wallet
// button.
export function checkoutIntentBody({ documentType, template = null, basePrice, discount = null, quantity = 1 }, payerEmail) {
  const isOffer = discount?.offer === true;
  const isUpsell = discount?.upsell === true;
  const finalAmount = discount ? discount.discountedPrice : basePrice;
  const discountValue = discount ? Math.max(0, basePrice - discount.discountedPrice) : 0;
  return {
    amount: (isOffer || isUpsell) ? basePrice : finalAmount,
    documentType,
    template,
    email: payerEmail || "",
    discountCode: (isOffer || isUpsell) ? null : (discount?.code || null),
    discountAmount: (isOffer || isUpsell) ? 0 : (discount ? parseFloat(discountValue.toFixed(2)) : 0),
    paywallOffer: isOffer,
    upsellOffer: isUpsell,
    quantity,
    // Session / landing page / campaign — stamped into the intent metadata so
    // the purchase links back to its visit in the admin funnel reports
    attribution: getAttribution(),
  };
}
export function checkoutAuthHeaders(discount) {
  if (discount?.offer !== true) return {};
  let token = "";
  try { token = localStorage.getItem("userToken") || ""; } catch {}
  return token ? { Authorization: `Bearer ${token}` } : {};
}

// In-app card checkout modal — sits on top of the document form/preview modals
// (z 10010) with the same portal + slide-up chrome. Card details live in
// Stripe Elements iframes (never in our inputs); only the name and email are
// regular Ionic inputs. On success the host modal stores its pending form data
// and hands off to /payment-success, which generates, downloads, emails, and
// shows the animated success screen.
export default function PaymentModal({
  docLabel, documentType, template = null, basePrice, discount = null,
  quantity = 1, onSuccess, onClose: onCloseNow, intentExtras = null,
}) {
  const onClose = (e) => dismissModal(onCloseNow, e); // animated exit, then the real close
  const stripe = useStripe();
  const elements = useElements();
  const isMobile = window.innerWidth < 768;

  const finalAmount = discount ? discount.discountedPrice : basePrice;
  const discountValue = discount ? Math.max(0, basePrice - discount.discountedPrice) : 0;
  // A 100%-off coupon makes the order free: no card form, just the download.
  const isFree = finalAmount <= 0;

  // Exit-intent offer: the discount is server-enforced. We send the BASE price
  // plus a paywallOffer flag and an auth token; the backend re-derives the
  // discount from the user's one-time window so the shown price is the charged
  // price and an expired offer can't be replayed.
  const offerAuthHeaders = () => checkoutAuthHeaders(discount);
  const intentBody = (payerEmail) => ({ ...checkoutIntentBody({ documentType, template, basePrice, discount, quantity }, payerEmail), ...(intentExtras || {}) });

  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  // A signed-in buyer's saved cards (Billing → Payment methods): offered as a
  // choice above the card form, the default preselected; "new" is the form.
  const [savedCards, setSavedCards] = useState([]);
  const [savedChoice, setSavedChoice] = useState("new");
  const usingSavedCard = savedChoice !== "new" && savedCards.some((c) => c.id === savedChoice);
  // A signed-in buyer entering a new card keeps it on the account (Billing → Payment methods) unless they untick this
  const signedIn = isUserLoggedIn();
  const [saveCard, setSaveCard] = useState(true);
  const [focusedBox, setFocusedBox] = useState(null);
  const [cardErrors, setCardErrors] = useState({});
  const [error, setError] = useState("");
  const [stage, setStage] = useState("form"); // form | paying | delivering | delivered | deliverFailed
  const busy = stage === "paying" || stage === "delivering";

  // Funnel: the payment sheet opening is "started checkout"
  useEffect(() => { trackEvent("checkout_start", { documentType, code: discount?.code || "" }); }, []); // eslint-disable-line

  // Signed in: the account's saved cards come up as a choice, and the
  // account's name and email fill the receipt fields
  useEffect(() => {
    if (!isUserLoggedIn() || isFree) return undefined;
    try {
      const u = JSON.parse(localStorage.getItem("userInfo") || "null");
      if (u) { setName((n) => n || u.name || ""); setEmail((e) => e || u.email || ""); }
    } catch { /* no stored account */ }
    let on = true;
    (async () => {
      try {
        const res = await fetch(`${BACKEND_URL}/api/stripe/payment-methods`, { headers: userHeaders() });
        const d = await res.json().catch(() => ({}));
        if (!on || !res.ok || !d.success) return;
        const cards = d.cards || [];
        setSavedCards(cards);
        if (cards.length) setSavedChoice(d.defaultId || cards[0].id);
      } catch { /* the card form stays */ }
    })();
    return () => { on = false; };
  }, []); // eslint-disable-line

  // Apple Pay (iOS/Safari) and Google Pay (Android/Chrome) via Stripe's
  // Payment Request button — it renders only when the device has a wallet.
  const { paymentRequest } = useWalletPayment({
    stripe, enabled: !isFree, label: docLabel, amount: finalAmount,
    intentBody, authHeaders: offerAuthHeaders, onSuccess, onClose, setStage, setError,
  });

  // Stripe iframes can't read our CSS variables, so resolve theme colors once.
  const stripeStyle = useMemo(() => {
    const dark = document.body.classList.contains("dark");
    return {
      base: {
        color: dark ? "#f4f5f8" : "#111827",
        fontSize: "15px",
        fontFamily: '"Manrope", "Helvetica Neue", Helvetica, Arial, sans-serif',
        "::placeholder": { color: dark ? "rgba(244,245,248,0.45)" : "#9ca3af" },
        iconColor: dark ? "#9ba0a6" : "#6b7280",
      },
      invalid: { color: "#f04141", iconColor: "#f04141" },
    };
  }, []);

  const cardStyle = { backgroundColor: "var(--ion-card-background)", borderRadius: 8, boxShadow: "rgba(0,0,0,0.18) 0px 4px 24px", padding: 16, display: "flex", flexDirection: "column", gap: 14, flexShrink: 0 /* the body scrolls; the cards keep their height */ };
  // Name/email render exactly like the Stripe element boxes: the container
  // carries the border/focus ring, the bare input matches Stripe's base style.
  const nativeInputStyle = {
    width: "100%", border: "none", outline: "none", background: "transparent",
    padding: 0, margin: 0, color: stripeStyle.base.color,
    fontSize: stripeStyle.base.fontSize, fontFamily: stripeStyle.base.fontFamily,
    lineHeight: "1.2", WebkitTextFillColor: stripeStyle.base.color,
  };
  const headingStyle = { fontWeight: 700, fontSize: "0.95rem", color: "var(--ion-text-color)" };
  const smallLabelStyle = { fontSize: "0.75rem", color: "var(--ion-color-medium)", marginBottom: 4, display: "block" };
  const boxStyle = (key) => ({
    padding: "13px 12px",
    borderRadius: 6,
    border: `1px solid ${cardErrors[key] ? "var(--ion-color-danger)" : focusedBox === key ? "var(--ion-color-primary)" : "var(--ion-color-step-300, rgba(0,0,0,0.23))"}`,
    boxShadow: focusedBox === key ? "0 0 0 1px var(--ion-color-primary)" : "none",
    background: "var(--ion-card-background)",
    transition: "border-color 0.15s ease, box-shadow 0.15s ease",
  });
  const elementProps = (key) => ({
    onFocus: () => setFocusedBox(key),
    onBlur: () => setFocusedBox((f) => (f === key ? null : f)),
    onChange: (ev) => {
      setCardErrors((prev) => ({ ...prev, [key]: ev.error ? ev.error.message : null }));
    },
    options: { style: stripeStyle, ...(key === "number" ? { showIcon: true } : {}) },
  });

  // Free order (100%-off coupon): skip Stripe entirely and deliver the
  // document straight away.
  const handleFreeDownload = async () => {
    setError("");
    let freeEmail = "";
    try { freeEmail = JSON.parse(localStorage.getItem("userInfo") || "null")?.email || ""; } catch {}
    setStage("delivering");
    try {
      await onSuccess({ email: freeEmail, name: "", paymentIntentId: `free_${Date.now().toString(36)}` });
      setStage("delivered");
      onClose();
    } catch (deliverErr) {
      console.error("Free download delivery failed:", deliverErr);
      setStage("deliverFailed");
    }
  };

  const handlePay = async () => {
    setError("");
    if (!stripe || !elements) { setError("Payment form is still loading. Please try again."); return; }
    if (!usingSavedCard && !name.trim()) { setError("Please enter the name on the card."); return; }
    if (!email.trim() || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim())) { setError("Please enter a valid email address."); return; }
    const firstCardError = usingSavedCard ? null : ["number", "expiry", "cvc"].map((k) => cardErrors[k]).find(Boolean);
    if (firstCardError) { setError(firstCardError); return; }

    setStage("paying");
    try {
      // A saved card is charged by the server against the account's Stripe
      // customer; a new card is confirmed here with the Stripe Elements.
      const keepCard = signedIn && !usingSavedCard && saveCard;
      const body = usingSavedCard
        ? { ...intentBody(email.trim()), savedPaymentMethodId: savedChoice }
        : { ...intentBody(email.trim()), ...(keepCard ? { saveCard: true } : {}) };
      const { ok, data } = await nativePost(`${BACKEND_URL}/api/stripe/create-payment-intent`, body, { ...offerAuthHeaders(), ...((usingSavedCard || keepCard) ? userHeaders() : {}) });
      if (!data) throw new Error("Server error. Please try again.");
      if (!ok || !data.clientSecret) throw new Error(data.detail || "Could not start the payment. Please try again.");

      const result = usingSavedCard
        ? (data.status === "succeeded"
          ? { paymentIntent: { id: data.paymentIntentId, status: "succeeded" } }
          : await stripe.confirmCardPayment(data.clientSecret)) // only a 3-D Secure step comes back here
        : await stripe.confirmCardPayment(data.clientSecret, {
          payment_method: {
            card: elements.getElement(CardNumberElement),
            billing_details: { name: name.trim(), email: email.trim() },
          },
        });
      if (result.error) throw new Error(result.error.message || "Your card was declined. Please try again.");
      if (result.paymentIntent?.status !== "succeeded") throw new Error("Payment did not complete. Please try again.");

      setStage("delivering");
      try {
        await onSuccess({ email: email.trim(), name: name.trim(), paymentIntentId: result.paymentIntent.id });
        setStage("delivered");
        onClose();
      } catch (deliverErr) {
        console.error("Post-payment delivery failed:", deliverErr);
        setStage("deliverFailed");
      }
    } catch (err) {
      const msg = err.message || "Payment failed. Please try again.";
        // Banned customers get the ban reason as a prominent toast too
        if (/banned from using MintSlip/i.test(msg)) showErrorToast(msg, { duration: 6000 });
        setError(msg);
      setStage("form");
    }
  };

  return createPortal(
    <div className="modal-backdrop" style={{ position: "fixed", inset: 0, zIndex: 10010, background: isMobile ? "var(--ion-background-color, #f2f2f7)" : "rgba(0,0,0,0.55)", display: "flex", alignItems: isMobile ? "stretch" : "center", justifyContent: isMobile ? "stretch" : "center" }}>
      <style>{`.msh-pay-input::placeholder { color: ${stripeStyle.base["::placeholder"].color}; }`}</style>
      <div className="modal-slide-up" style={{ background: "var(--ion-background-color, #f2f2f7)", color: "var(--ion-text-color)", display: "flex", flexDirection: "column", width: "100%", maxWidth: isMobile ? "100%" : 600, height: isMobile ? "100%" : "auto", maxHeight: isMobile ? "100%" : "90vh", overflow: "hidden" }}>
        <IonHeader>
          <IonToolbar style={{ "--background": "var(--ion-card-background)", "--color": "var(--ion-text-color)" }}>
            <IonButtons slot="start">
              <IonButton fill="clear" shape="round" onClick={onClose} disabled={busy}>
                <span slot="icon-only" style={{ display: "inline-flex", alignItems: "center", justifyContent: "center", lineHeight: 0, flexShrink: 0, fontSize: "1.8em", color: "var(--ion-text-color)" }}>
                  <IonIcon icon={closeOutline} style={{ fontSize: "inherit", color: "inherit", pointerEvents: "none" }} />
                </span>
              </IonButton>
            </IonButtons>
            <IonTitle style={{ fontWeight: 700 }}>Secure Checkout</IonTitle>
          </IonToolbar>
        </IonHeader>

        {stage === "delivering" || stage === "delivered" ? (
          <div style={{ display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", padding: "56px 24px", gap: 12 }}>
            <IonIcon icon={checkmarkCircle} color="success" style={{ fontSize: "3rem" }} />
            <div style={{ fontWeight: 700, fontSize: "1.05rem" }}>{isFree ? "Order confirmed!" : "Payment successful!"}</div>
            <div style={{ display: "flex", alignItems: "center", gap: 8, color: "var(--ion-color-medium)", fontSize: "0.85rem" }}>
              <IonSpinner name="crescent" style={{ width: 18, height: 18 }} />
              Preparing your download…
            </div>
          </div>
        ) : stage === "deliverFailed" ? (
          <div style={{ display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", padding: "48px 24px", gap: 12, textAlign: "center" }}>
            <IonIcon icon={checkmarkCircle} color="success" style={{ fontSize: "3rem" }} />
            <div style={{ fontWeight: 700, fontSize: "1.05rem" }}>{isFree ? "Order confirmed" : "Payment received"}</div>
            <div style={{ color: "var(--ion-color-medium)", fontSize: "0.85rem", maxWidth: 320 }}>
              {isFree
                ? "Preparing the download failed. Please try again or contact support and we'll send your document."
                : "Your payment went through, but preparing the download failed. Your purchase is recorded. Please contact support and we'll send your document."}
            </div>
            <IonButton color="light" onClick={onClose} style={{ marginTop: 8 }}>Close</IonButton>
          </div>
        ) : (
          <div style={{ flex: 1, overflowY: "auto", padding: 16, display: "flex", flexDirection: "column", gap: 16 }}>
            {/* Order summary */}
            <div style={cardStyle}>
              <div style={headingStyle}>Order summary</div>
              <div style={{ display: "flex", justifyContent: "space-between", fontSize: "0.875rem" }}>
                <span>{docLabel}</span>
                <span style={{ color: "var(--ion-color-success)", fontWeight: 600 }}>${basePrice.toFixed(2)}</span>
              </div>
              {discount && (
                <div style={{ display: "flex", justifyContent: "space-between", fontSize: "0.875rem", color: "var(--ion-color-success-shade)" }}>
                  <span>
                    {discount.code || (discount.upsell ? "Matching-document offer" : discount.offer ? "Last-chance offer" : "Discount")}
                    {": "}{discount.discountPercent ?? Math.round((discountValue / (basePrice || 1)) * 100)}% off
                  </span>
                  <span>−${discountValue.toFixed(2)}</span>
                </div>
              )}
              <div style={{ borderTop: "1px solid var(--ion-color-step-150, rgba(0,0,0,0.08))", paddingTop: 10, display: "flex", justifyContent: "space-between", fontWeight: 700 }}>
                <span>Total</span>
                <span style={{ color: "var(--ion-color-success)" }}>${finalAmount.toFixed(2)}</span>
              </div>
              <div style={{ fontSize: "0.72rem", color: "var(--ion-color-medium)" }}>One-time payment · instant download</div>
            </div>

            {/* Payment details — or a free download when a coupon covers it all */}
            {isFree ? (
            <div style={cardStyle}>
              <div style={headingStyle}>No payment needed</div>
              <div style={{ fontSize: "0.85rem", color: "var(--ion-color-medium)" }}>
                Your coupon covers the full price. Download your document for free.
              </div>
              {error && <IonNote color="danger" style={{ display: "block", fontSize: "0.8rem" }}>{error}</IonNote>}
              <IonButton expand="block" color="success" style={{ "--border-radius": "4px" }} onClick={handleFreeDownload} disabled={busy}>
                <IonIcon icon={cloudDownloadOutline} slot="start" />
                Download (Free)
              </IonButton>
            </div>
            ) : (
            <div style={cardStyle}>
              <div style={headingStyle}>Payment details</div>
              {paymentRequest && (
                <>
                  <PaymentRequestButtonElement
                    options={{ paymentRequest, style: { paymentRequestButton: { theme: "dark", height: "44px" } } }}
                  />
                  <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                    <div style={{ flex: 1, height: 1, background: "var(--ion-color-step-150, rgba(0,0,0,0.12))" }} />
                    <span style={{ fontSize: "0.72rem", color: "var(--ion-color-medium)", whiteSpace: "nowrap" }}>or pay with card</span>
                    <div style={{ flex: 1, height: 1, background: "var(--ion-color-step-150, rgba(0,0,0,0.12))" }} />
                  </div>
                </>
              )}
              {savedCards.length > 0 && (
                /* the saved cards read like the role picker in Add Employee: a sub-bar, then rows of title + note with the radio at the end */
                <div className="saved-cards-section">
                  <div className="admin-subbar saved-cards-head">Your cards</div>
                  <IonRadioGroup value={savedChoice} onIonChange={(e) => setSavedChoice(e.detail.value)} className="saved-cards-roles">
                    {savedCards.map((c) => (
                      <IonItem key={c.id} disabled={busy} className="saved-card-row">
                        <IonRadio value={c.id} labelPlacement="start" justify="space-between" className="saved-card-radio" aria-label={`Pay with ${cardTitle(c)}`}>
                          <div className="emp-role-text"><h3>{cardName(c)}</h3><p>{cardNote(c, c.isDefault)}</p></div>
                        </IonRadio>
                      </IonItem>
                    ))}
                    <IonItem lines="none" disabled={busy} className="saved-card-row">
                      <IonRadio value="new" labelPlacement="start" justify="space-between" className="saved-card-radio" aria-label="Use a new card">
                        <div className="emp-role-text"><h3>Use a new card</h3><p>Enter a card number below</p></div>
                      </IonRadio>
                    </IonItem>
                  </IonRadioGroup>
                </div>
              )}
              {!usingSavedCard && (
              <div>
                <span style={smallLabelStyle}>Name on card</span>
                <div style={boxStyle("name")}>
                  <input
                    className="msh-pay-input"
                    type="text" value={name} autoComplete="off"
                    disabled={busy} style={nativeInputStyle}
                    onFocus={() => setFocusedBox("name")}
                    onBlur={() => setFocusedBox((f) => (f === "name" ? null : f))}
                    onChange={(e) => setName(e.target.value)}
                  />
                </div>
              </div>
              )}
              <div>
                <span style={smallLabelStyle}>Email</span>
                <div style={boxStyle("email")}>
                  <input
                    className="msh-pay-input"
                    type="email" value={email} autoComplete="off"
                    disabled={busy} style={nativeInputStyle}
                    onFocus={() => setFocusedBox("email")}
                    onBlur={() => setFocusedBox((f) => (f === "email" ? null : f))}
                    onChange={(e) => setEmail(e.target.value)}
                  />
                </div>
                <span style={{ fontSize: "0.7rem", color: "var(--ion-color-medium)", marginTop: 4, display: "block" }}>
                  Your document and receipt are sent here
                </span>
              </div>
              {/* the card fields stay mounted (Stripe Elements keep their state) and hide while a saved card is picked */}
              <div style={usingSavedCard ? { display: "none" } : undefined} className="new-card-fields">
              <div>
                <span style={smallLabelStyle}>Card number</span>
                <div style={boxStyle("number")}>
                  <CardNumberElement {...elementProps("number")} />
                </div>
                {cardErrors.number && <IonNote color="danger" style={{ display: "block", marginTop: 4, fontSize: "0.72rem" }}>{cardErrors.number}</IonNote>}
              </div>
              <div style={{ display: "flex", gap: 10, marginTop: 14 }}>
                <div style={{ flex: 1 }}>
                  <span style={smallLabelStyle}>Expiry</span>
                  <div style={boxStyle("expiry")}>
                    <CardExpiryElement {...elementProps("expiry")} />
                  </div>
                  {cardErrors.expiry && <IonNote color="danger" style={{ display: "block", marginTop: 4, fontSize: "0.72rem" }}>{cardErrors.expiry}</IonNote>}
                </div>
                <div style={{ flex: 1 }}>
                  <span style={smallLabelStyle}>CVC</span>
                  <div style={boxStyle("cvc")}>
                    <CardCvcElement {...elementProps("cvc")} />
                  </div>
                  {cardErrors.cvc && <IonNote color="danger" style={{ display: "block", marginTop: 4, fontSize: "0.72rem" }}>{cardErrors.cvc}</IonNote>}
                </div>
              </div>
              {signedIn && (
                <label className="save-card-row">
                  <input type="checkbox" checked={saveCard} disabled={busy} onChange={(e) => setSaveCard(e.target.checked)} />
                  <span>Save this card to my account for next time</span>
                </label>
              )}
              </div>

              {error && <IonNote color="danger" style={{ display: "block", fontSize: "0.8rem" }}>{error}</IonNote>}

              <IonButton expand="block" color="success" style={{ "--border-radius": "4px" }} onClick={handlePay} disabled={busy}>
                {stage === "paying"
                  ? <IonSpinner name="crescent" />
                  : <><IonIcon icon={lockClosedOutline} slot="start" />Pay ${finalAmount.toFixed(2)}</>}
              </IonButton>

              <div style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 6, fontSize: "0.72rem", color: "var(--ion-color-medium)" }}>
                <IonIcon icon={lockClosedOutline} style={{ fontSize: "0.85rem" }} />
                Payments are encrypted and processed securely by Stripe
              </div>
            </div>
            )}
          </div>
        )}
      </div>
    </div>,
    document.querySelector("ion-app") || document.body
  );
}
